import { FC, PropsWithChildren, createContext, useEffect, useState } from 'react';

import { Account, LoginInfo } from '@/interfaces/LoginInterface';
import { CacheUtil } from '@/util/CacheUtil';
import { getAccess } from '@/apis/AccessApi';
import { useAsync } from 'react-use';

interface UserContextType extends Account {
    role_name: string;
    feature: string[];
    phone: string;
    email: string;
    photoUrl?: string;
    id: number;
    setUserInfo: (userInfo: Account) => void;
    setRealName: (real_name: string) => void;
}

const UserContext = createContext<Partial<UserContextType>>({});

const UserProvider: FC<PropsWithChildren> = ({ children }) => {
    const [user, setUser] = useState<LoginInfo>(() => CacheUtil.getLoginInfo());
    // Use cached features if available, otherwise fetch from API
    const { value: personalAccess } = useAsync(
        async () => { 
            if (user.user_name && !user.features) {
                return await getAccess(String(user.role_id));
            }
            return null;
        },
        [user],
    );

    useEffect(() => {
        CacheUtil.setLoginInfo(user);
    }, [user]);
    
    // Prefer cached features, fallback to API response if not available
    const features = user.features || personalAccess?.data?.data;
    
    return (
        <UserContext.Provider
            value={{
                id: user.id,
                user_name: user.user_name,
                real_name: user.real_name,
                role_id: user.role_id,
                role_name: user.role_name || '',
                feature: features || [],
                photoUrl: '',
                setUserInfo: (userInfo) => setUser(userInfo as LoginInfo),
                setRealName: (real_name: string) => setUser({ ...user, real_name: real_name }),
            }}
        >
            {children}
        </UserContext.Provider>
    );
};

export default UserProvider;
export { UserContext };
